import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Stethoscope, ExternalLink } from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
} from "recharts";
import { formatNumber } from "@/lib/formatters";
import { tooltipStyle } from "./oversiktConstants";

const pst = (v) => (v == null ? "—" : `${String(v).replace(".", ",")} %`);
const tal = (v) => (v == null ? "—" : formatNumber(v));

const Kort = ({ tittel, verdi, under, farge = "text-[#1F2A36]", testId }) => (
  <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-[0.08em] text-[#8AA4BF] mb-1">{tittel}</p>
    <p className={`text-xl font-light tabular-nums ${farge}`}>{verdi}</p>
    {under && <p className="text-xs text-[#8AA4BF] mt-0.5">{under}</p>}
  </div>
);

const Rangering = ({ rang, tittel, format, testId }) => {
  if (!rang) return null;
  return (
    <p className="text-xs text-[#4B647F]" data-testid={testId}>
      <span className="text-[#8AA4BF]">{tittel}:</span> nummer {rang.rank} av {rang.count} kommunar i landet (
      {rang.hoeg_er_verst ? "høgast" : "lågast"} først). Verst: {rang.verst.namn} {format(rang.verst.verdi)} · best:{" "}
      {rang.best.namn} {format(rang.best.verdi)}
    </p>
  );
};

export const FastlegeSection = ({ data, kommuneName }) => {
  const latest = data?.latest;
  if (!latest) return null;
  if (data?.har_fastlegar === false) {
    return (
      <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-6" id="fastlege-card" data-testid="fastlege-card">
        <CardHeader className="border-b border-[#E6E9F0] pb-4">
          <CardTitle className="text-base font-medium text-[#1F2A36] flex items-center gap-2">
            <Stethoscope className="w-4 h-4 text-[#103766]" /> Fastlegedekning
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <p className="text-sm text-[#4B647F]" data-testid="fastlege-ingen-data">
            SSB har ingen registrerte fastlegeavtalar i {kommuneName || "kommunen"}. Det tyder som regel at innbyggjarane
            står på lister i nabokommunen, eller at legetenesta er organisert i eit interkommunalt samarbeid.
            {latest.konsultasjonar_per_person != null && (
              <>
                {" "}
                Innbyggjarane hadde {String(latest.konsultasjonar_per_person).replace(".", ",")} fastlegekonsultasjonar
                kvar i {latest.year}.
              </>
            )}
          </p>
        </CardContent>
      </Card>
    );
  }
  const { history = [], land, gruppe, fylke_name, rangering = {} } = data;
  const landByYear = Object.fromEntries((data.land_history || []).map((r) => [r.year, r]));
  const gruppeByYear = Object.fromEntries((data.gruppe_history || []).map((r) => [r.year, r]));
  const gruppeNamn = fylke_name || "fylket";

  const chart = history.map((r) => ({
    year: r.year,
    [kommuneName || "Kommunen"]: r.andel_utan_lege,
    [gruppeNamn]: gruppeByYear[r.year]?.andel_utan_lege ?? null,
    Landet: landByYear[r.year]?.andel_utan_lege ?? null,
  }));

  const listeChart = history.map((r) => ({
    year: r.year,
    [kommuneName || "Kommunen"]: r.listelengde,
    Landet: landByYear[r.year]?.listelengde ?? null,
  }));

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-6" id="fastlege-card" data-testid="fastlege-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-base font-medium text-[#1F2A36] flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-[#103766]" /> Fastlegedekning
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Kor mange som står på ei fastlegeliste utan fast lege, kor lange listene er og kor mykje ledig kapasitet
          fastlegane har. Tal for {latest.year}.
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Kort
            testId="fastlege-utan-lege"
            tittel="Utan fast lege"
            verdi={pst(latest.andel_utan_lege)}
            under={`${tal(latest.pasientar_utan_lege)} innbyggjarar · landet ${pst(land?.andel_utan_lege)}`}
            farge={
              latest.andel_utan_lege != null && land?.andel_utan_lege != null && latest.andel_utan_lege > land.andel_utan_lege
                ? "text-[#C0392B]"
                : "text-[#1F2A36]"
            }
          />
          <Kort
            testId="fastlege-listelengde"
            tittel="Innbyggjarar per fastlege"
            verdi={tal(latest.listelengde)}
            under={`Landet ${tal(land?.listelengde)}${gruppe ? ` · ${gruppeNamn} ${tal(gruppe.listelengde)}` : ""}`}
          />
          <Kort
            testId="fastlege-avtaler"
            tittel="Fastlegeavtalar"
            verdi={tal(latest.avtaler)}
            under={`${tal(latest.lister_utan_lege)} lister utan fast lege`}
          />
          <Kort
            testId="fastlege-kapasitet"
            tittel="Reservekapasitet (100 = fullt)"
            verdi={tal(latest.reservekapasitet)}
            under={`${tal(latest.opne_lister)} opne lister · ${
              latest.konsultasjonar_per_person != null
                ? `${String(latest.konsultasjonar_per_person).replace(".", ",")} konsultasjonar per innbyggjar`
                : ""
            }`}
          />
        </div>

        <div className="h-[280px]" data-testid="fastlege-chart">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
            Del av innbyggjarane utan fast lege
          </p>
          <ResponsiveContainer width="100%" height="88%">
            <ComposedChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
              <YAxis stroke="#8AA4BF" fontSize={11} width={44} tickFormatter={(v) => `${v} %`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => pst(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
              <Line
                type="monotone"
                dataKey={kommuneName || "Kommunen"}
                stroke="#C0392B"
                strokeWidth={2.5}
                dot={{ r: 3 }}
                connectNulls
              />
              <Line type="monotone" dataKey={gruppeNamn} stroke="#103766" strokeWidth={1.5} dot={false} connectNulls />
              <Line type="monotone" dataKey="Landet" stroke="#9FB6CC" strokeWidth={1.5} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="h-[260px]" data-testid="fastlege-listelengde-chart">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
            Innbyggjarar per fastlege
          </p>
          <ResponsiveContainer width="100%" height="88%">
            <ComposedChart data={listeChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
              <YAxis stroke="#8AA4BF" fontSize={11} width={48} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => tal(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
              <Line
                type="monotone"
                dataKey={kommuneName || "Kommunen"}
                stroke="#103766"
                strokeWidth={2.5}
                dot={{ r: 3 }}
                connectNulls
              />
              <Line type="monotone" dataKey="Landet" stroke="#9FB6CC" strokeWidth={1.5} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-1">
          <Rangering
            rang={rangering.andel_utan_lege}
            tittel="Innbyggjarar utan fast lege"
            format={(v) => pst(v)}
            testId="fastlege-rang-utan-lege"
          />
          <Rangering
            rang={rangering.listelengde}
            tittel="Innbyggjarar per fastlege"
            format={(v) => tal(v)}
            testId="fastlege-rang-listelengde"
          />
          <Rangering
            rang={rangering.reservekapasitet}
            tittel="Reservekapasitet (100 = fullt)"
            format={(v) => tal(v)}
            testId="fastlege-rang-kapasitet"
          />
        </div>

        <p className="text-xs text-[#8AA4BF]">
          Kjelde:{" "}
          <a
            href="https://www.ssb.no/statbank/table/12005"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#103766] hover:underline inline-flex items-center gap-1"
          >
            SSB tabell 12005 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          – fastlegelister og fastlegekonsultasjonar, {history[0]?.year}–{latest.year}.
        </p>
      </CardContent>
    </Card>
  );
};

export default FastlegeSection;
